<template>
  <q-dialog v-model="isOpen" persistent>
    <q-card style="min-width: 600px">
      <q-card-section>
        <div class="text-h6">
          Add Controller to {{ isEquipment ? 'Equipment' : 'DDC Board' }}
        </div>
        <div class="text-subtitle2 text-grey-7">
          {{ parentEquipment?.name }}
        </div>
      </q-card-section>

      <q-card-section>
        <!-- Auto-assign notice for equipment -->
        <q-banner v-if="isEquipment" class="bg-blue-1 text-blue-9 q-mb-md" rounded>
          <template #avatar>
            <q-icon name="info" color="blue" />
          </template>
          This equipment will be automatically assigned to the controller
        </q-banner>

        <!-- Controller template selection -->
        <q-select
          v-model="selectedTemplate"
          :options="controllerTemplates"
          option-label="name"
          label="Controller Template"
          outlined
          dense
          :loading="loadingTemplates"
        >
          <template #option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section>
                <q-item-label>{{ scope.opt.name }}</q-item-label>
                <q-item-label caption>
                  {{ scope.opt.manufacturer }} - {{ scope.opt.model_number }}
                </q-item-label>
                <q-item-label v-if="scope.opt.io_capacity" caption>
                  I/O: {{ formatIoCapacity(scope.opt.io_capacity) }}
                </q-item-label>
              </q-item-section>
              <q-item-section v-if="scope.opt.list_price" side>
                <q-item-label caption>${{ scope.opt.list_price }}</q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>

        <!-- Controller name -->
        <q-input
          v-model="controllerName"
          label="Controller Name"
          outlined
          dense
          class="q-mt-md"
          hint="e.g., 'Controller 1' or 'AHU 3 Controller'"
        />
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="closeDialog" />
        <q-btn
          color="primary"
          label="Add Controller"
          :loading="loading"
          :disable="!selectedTemplate || !controllerName"
          @click="addController"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { api } from '@/services/api';

  const $q = useQuasar();

  const props = defineProps({
    modelValue: Boolean,
    parentEquipment: Object,
    siteId: [Number, String],
  });

  const emit = defineEmits(['update:modelValue', 'controller-added']);

  const isOpen = computed({
    get: () => props.modelValue,
    set: (val) => emit('update:modelValue', val),
  });

  // Check if parent is equipment (not DDC board)
  const isEquipment = computed(() => {
    if (!props.parentEquipment?.brick_class) {return false;}
    return !props.parentEquipment.brick_class.includes('DDC_Panel') &&
      !props.parentEquipment.brick_class.includes('BMS_Panel');
  });

  const controllerTemplates = ref([]);
  const selectedTemplate = ref(null);
  const controllerName = ref('');
  const loading = ref(false);
  const loadingTemplates = ref(false);

  // Load controller templates
  const loadTemplates = async () => {
    loadingTemplates.value = true;
    try {
      const response = await api.get('v2/controller-templates', {
        is_active: true,
      });
      controllerTemplates.value = response.data || response;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load controller templates',
        position: 'top',
      });
    } finally {
      loadingTemplates.value = false;
    }
  };

  const formatIoCapacity = (io) => {
    if (!io) {return 'N/A';}
    return `${io.AI || 0} AI, ${io.AO || 0} AO, ${io.DI || 0} DI, ${io.DO || 0} DO`;
  };

  const addController = async () => {
    loading.value = true;
    try {
      // Step 1: Instantiate controller from template
      const instantiateData = {
        site_id: props.siteId,
        name: controllerName.value,
        parent_equipment_id: props.parentEquipment.id,
        copy_io_points: true,
      };

      const controllerResponse = await api.post(
        `v2/controller-templates/${selectedTemplate.value.id}/instantiate`,
        instantiateData,
      );

      const newController = controllerResponse.data;

      // Step 2: If parent is equipment (not DDC board), auto-assign equipment to controller
      if (isEquipment.value) {
        try {
          await api.post(
            `v2/site-equipment/${props.parentEquipment.id}/assign-controller`,
            { controller_id: newController.id },
          );

          $q.notify({
            type: 'positive',
            message: `Controller "${controllerName.value}" added and equipment assigned`,
            position: 'top',
          });
        } catch (assignError) {
          $q.notify({
            type: 'warning',
            message: `Controller added but auto-assignment failed: ${assignError.message}`,
            position: 'top',
          });
        }
      } else {
        $q.notify({
          type: 'positive',
          message: `Controller "${controllerName.value}" added to DDC board`,
          position: 'top',
        });
      }

      emit('controller-added', newController);
      closeDialog();
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to add controller: ${error.message || 'Unknown error'}`,
        position: 'top',
      });
    } finally {
      loading.value = false;
    }
  };

  const closeDialog = () => {
    isOpen.value = false;
    selectedTemplate.value = null;
    controllerName.value = '';
  };

  // Load templates when dialog opens
  watch(() => props.modelValue, (newVal) => {
    if (newVal) {
      loadTemplates();
    }
  });
</script>
